import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ArrowLeft, BookOpen, FileText, Download } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import ReportToolbar from '../../components/ReportToolbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LedgerPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(undefined);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('erp_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/accounts`, { headers });
        const list = (res.data || []).slice().sort((a, b) => (a.code || '').localeCompare(b.code || ''));
        setAccounts(list);
        if (list.length && !accountId) setAccountId(list[0].id);
      } catch (e) {
        toast.error('Failed to load accounts');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLedger = async () => {
    if (!accountId) return toast.error('Please select an account');
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      const res = await axios.get(`${API_URL}/api/accounts/${accountId}/ledger?${params}`, { headers });
      setLedger(res.data);
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.detail || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (accountId) fetchLedger(); /* eslint-disable-next-line */ }, [accountId]);

  const formatCurrency = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const downloadCSV = () => {
    if (!ledger) return;
    const rows = [
      ['Date', 'Reference', 'Description', 'Debit', 'Credit', 'Balance'],
      ...((ledger.entries || []).map(e => [
        e.date, e.reference || '', e.description || '',
        e.debit, e.credit, e.balance
      ]))
    ];
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ledger_${ledger.account?.code || accountId}_${fromDate}_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8 p-6 lg:p-8" data-testid="ledger-page">
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between flex-wrap gap-4"
      >
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2" data-testid="ledger-back">
            <ArrowLeft size={14} className="mr-1" /> Back
          </Button>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold flex items-center gap-2">
            <BookOpen size={32} className="text-primary" /> General Ledger
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Account-level transaction history with running balance.
          </p>
        </div>
        {ledger && (
          <Button variant="outline" onClick={downloadCSV} data-testid="ledger-csv">
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
        )}
      </motion.div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Account</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger data-testid="ledger-account-select"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.code} — {a.name} ({a.account_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="ledger-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="ledger-to" />
            </div>
            <div className="flex items-end">
              <Button onClick={fetchLedger} disabled={loading} data-testid="ledger-fetch">
                {loading ? 'Loading…' : 'Refresh'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Universal export toolbar — adds PDF + Excel + Print */}
      <ReportToolbar
        testidPrefix="ledger-toolbar"
        downloadBaseName={`general-ledger-${fromDate}_to_${toDate}`}
        exportUrl={(fmt) => {
          const params = new URLSearchParams();
          if (accountId) params.set('account_id', accountId);
          if (fromDate) params.set('start_date', fromDate);
          if (toDate) params.set('end_date', toDate);
          return `${API_URL}/api/reports/general-ledger.${fmt}?${params.toString()}`;
        }}
      />

      {ledger && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText size={18} />
              {ledger.account?.code} — {ledger.account?.name}
              <Badge variant="outline" className="ml-2">{ledger.account?.account_type}</Badge>
            </CardTitle>
            <div className="text-xs text-muted-foreground">
              Opening balance: <span className="font-mono">{formatCurrency(ledger.opening_balance)}</span>
              {' · '}
              Closing balance: <span className="font-mono font-semibold">{formatCurrency(ledger.closing_balance)}</span>
              {' · '}
              {(ledger.entries || []).length} transaction(s)
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="text-right">Running Balance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={5} className="font-medium">Opening balance</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatCurrency(ledger.opening_balance)}</TableCell>
                  </TableRow>
                  {(ledger.entries || []).map((e, i) => (
                    <TableRow key={i} className="hover:bg-muted/30" data-testid={`ledger-row-${i}`}>
                      <TableCell className="font-mono text-xs">{e.date}</TableCell>
                      <TableCell className="font-mono text-xs">{e.reference || '—'}</TableCell>
                      <TableCell className="text-sm">{e.description || '—'}</TableCell>
                      <TableCell className="text-right font-mono">{e.debit > 0 ? formatCurrency(e.debit) : '—'}</TableCell>
                      <TableCell className="text-right font-mono">{e.credit > 0 ? formatCurrency(e.credit) : '—'}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(e.balance)}</TableCell>
                    </TableRow>
                  ))}
                  {(!ledger.entries || ledger.entries.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                        No transactions in this period.
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow className="bg-muted/50 border-t-2">
                    <TableCell colSpan={5} className="font-semibold">Closing balance</TableCell>
                    <TableCell className="text-right font-mono font-bold">{formatCurrency(ledger.closing_balance)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
